import { useTranslation } from 'react-i18next'
import { Alert, Box, H3, H5, Stack, StackRow, Button, Grid } from '@dhl-ui/components'
import { DuplicateIcon, DownloadIcon } from '@dhl-ui/icons'
import { useGetDtsFlow, type DtsFlowSummaryDto } from '@dhl/mdp-api'
import { InboundConnector, OutboundConnector, Converter } from '~/modules/ppp/submodules/relations/components'
import { DataWrapper, DataRow } from '~/modules/ppp/submodules/relations/components/data'
import { useClipboard } from '~/common'
import { downloadJson } from '~/common/util'
import { presentDateOrDefault } from '~/modules/ppp/submodules/relations/utils'

type Props = {
  flow: DtsFlowSummaryDto
}

const mt = 2 // used all over the place for margin-top, so we define it here to avoid magic numbers

export const PppRelationFlow = ({ flow: { flowName } }: Props) => {
  const { data: flow, isError, isSuccess, isLoading } = useGetDtsFlow(flowName)
  const { t } = useTranslation()
  const [hasClipboard, copyToClipboard] = useClipboard()

  if (isLoading) {
    return null
  }

  const flowAsJson = flow ? JSON.stringify(flow, null, 2) : '{}'

  return (
    <Box sx={{ mt: 1 }}>
      <H3>{flowName}</H3>
      {isError && (
        <Alert variant="standard" severity="error">
          {t('admin.pppRelationsPage.flow.error.message')}
        </Alert>
      )}
      {isSuccess && flow && (
        <Stack>
          <StackRow>
            {hasClipboard && (
              <Button
                onClick={() => copyToClipboard(flowAsJson)}
                title={t('admin.pppRelationsPage.copyFlowToClipboard')}
                variant="outlined"
                size="small"
                color="primary"
              >
                <DuplicateIcon />
              </Button>
            )}
            <Button
              onClick={() => downloadJson(`${flowName}.json`, flowAsJson)}
              title={t('admin.pppRelationsPage.downloadFlow')}
              variant="outlined"
              size="small"
            >
              <DownloadIcon />
            </Button>
          </StackRow>
          <StackRow sx={{ mt }}>
            <DataWrapper data-testid="ppp-relation-flow-data-wrapper">
              <DataRow title="flowName" payload={flow.flowName} withCopy />
              <DataRow
                title={t('admin.pppRelationsPage.flowsTable.enabled')}
                payload={flow.enabled ? t('admin.pppRelationsPage.enabled') : t('admin.pppRelationsPage.disabled')}
              />
              <DataRow title="activeSince" payload={presentDateOrDefault(flow.activeSince)} />
              <DataRow title="activeUntil" payload={presentDateOrDefault(flow.activeUntil)} />
            </DataWrapper>
          </StackRow>
          <Grid container direction="row" columns={3} gap={2} sx={{ mt }}>
            {flow.inboundConnector && (
              <Grid gridColumn={1} flexGrow={1} sx={{ maxWidth: '50%' }}>
                <H5>Inbound Connector</H5>
                <InboundConnector connector={flow.inboundConnector} />
              </Grid>
            )}
            {flow.outboundConnector && (
              <Grid gridColumn={2} flexGrow={1} sx={{ maxWidth: '50%' }}>
                <H5>Outbound Connector</H5>
                <OutboundConnector connector={flow.outboundConnector} />
              </Grid>
            )}
            {flow.converter && (
              <Grid gridColumn={3} flexGrow={1} sx={{ maxWidth: '50%' }}>
                <H5>Converter</H5>
                <Converter connector={flow.converter} />
              </Grid>
            )}
          </Grid>
        </Stack>
      )}
    </Box>
  )
}
